export const DEFAULT_APPEARANCE = {
  palette: 'lilac-mist',
  font: 'manrope',
  accent: 'lilac',
  surface_style: 'balanced',
};

export const APPEARANCE_PALETTES = [
  { id: 'lilac-mist', name: 'Lilac mist', tone: 'Light · default', background: '#f6f4fb', surface: '#ffffff', sidebar: '#ffffff', text: '#1e1a33', border: '#e6e1f2' },
  { id: 'clean-slate', name: 'Clean slate', tone: 'Light · neutral', background: '#f7f8fa', surface: '#ffffff', sidebar: '#ffffff', text: '#111827', border: '#e3e6eb' },
  { id: 'executive-blue', name: 'Executive', tone: 'Light · navy menu', background: '#f3f6fa', surface: '#ffffff', sidebar: '#0f172a', text: '#172033', border: '#dce4ee' },
  { id: 'warm-sand', name: 'Warm sand', tone: 'Light · warm', background: '#f6f2ea', surface: '#fffdf9', sidebar: '#fffdf9', text: '#2a2620', border: '#e8e0d2' },
  { id: 'lilac-night', name: 'Lilac night', tone: 'Dark · brand', background: '#0f0d1a', surface: '#181530', sidebar: '#120f22', text: '#e9e4f5', border: '#2c2850', dark: true },
  { id: 'forest', name: 'Forest', tone: 'Dark · green', background: '#0f1713', surface: '#16211b', sidebar: '#121c17', text: '#e6eee8', border: '#273a30', dark: true },
  { id: 'carbon', name: 'Carbon', tone: 'Dark · neutral', background: '#141516', surface: '#1d1f21', sidebar: '#0e0f10', text: '#f0f1f2', border: '#2c2f33', dark: true },
];

export const APPEARANCE_FONTS = [
  { id: 'manrope', name: 'Manrope', character: 'Qflow default', family: "'Manrope', sans-serif" },
  { id: 'inter', name: 'Inter', character: 'Crisp and familiar', family: "'Inter', sans-serif" },
  { id: 'ibm-plex', name: 'IBM Plex Sans', character: 'Technical and assured', family: "'IBM Plex Sans', sans-serif" },
  { id: 'source-sans', name: 'Source Sans 3', character: 'Most readable', family: "'Source Sans 3', sans-serif" },
];

// `color` is the shade used on light themes; `bright` is used on dark themes.
export const APPEARANCE_ACCENTS = [
  { id: 'lilac', name: 'Lilac', color: '#6d4fd6', bright: '#a78bfa' },
  { id: 'azure', name: 'Azure', color: '#2563eb', bright: '#7aa7ff' },
  { id: 'teal', name: 'Teal', color: '#0f766e', bright: '#5ec9b9' },
  { id: 'emerald', name: 'Emerald', color: '#15803d', bright: '#6bd38f' },
  { id: 'rose', name: 'Rose', color: '#be3455', bright: '#f49ab0' },
  { id: 'amber', name: 'Amber', color: '#b45309', bright: '#f2b26b' },
  { id: 'mint', name: 'Mint', color: '#15936a', bright: '#6fd3a0' },
];

export const APPEARANCE_SURFACES = [
  { id: 'soft', name: 'Soft', description: 'Rounded, relaxed surfaces' },
  { id: 'balanced', name: 'Balanced', description: 'Mature everyday default' },
  { id: 'sharp', name: 'Sharp', description: 'Tighter, precise geometry' },
];

// Retired choices map to their nearest current option (mirrors the backend).
export const LEGACY_APPEARANCE_CHOICES = {
  palette: { 'deep-teal': 'clean-slate', 'warm-graphite': 'warm-sand', 'soft-sand': 'warm-sand', 'mist-lavender': 'lilac-mist', 'midnight-blue': 'lilac-night' },
  font: { 'plus-jakarta': 'manrope', 'work-sans': 'inter' },
  accent: { indigo: 'lilac', violet: 'lilac', graphite: 'azure' },
};

const paletteIds = new Set(APPEARANCE_PALETTES.map(({ id }) => id));
const fontIds = new Set(APPEARANCE_FONTS.map(({ id }) => id));
const accentIds = new Set(APPEARANCE_ACCENTS.map(({ id }) => id));
const surfaceIds = new Set(APPEARANCE_SURFACES.map(({ id }) => id));
const current = (field, value) => LEGACY_APPEARANCE_CHOICES[field][value] || value;

export function normalizeAppearance(value = {}) {
  const palette = current('palette', value.palette);
  const font = current('font', value.font);
  const accent = current('accent', value.accent);
  return {
    palette: paletteIds.has(palette) ? palette : DEFAULT_APPEARANCE.palette,
    font: fontIds.has(font) ? font : DEFAULT_APPEARANCE.font,
    accent: accentIds.has(accent) ? accent : DEFAULT_APPEARANCE.accent,
    surface_style: surfaceIds.has(value.surface_style) ? value.surface_style : DEFAULT_APPEARANCE.surface_style,
  };
}

export function applyAppearance(value = DEFAULT_APPEARANCE) {
  const appearance = normalizeAppearance(value);
  const root = document.documentElement;
  root.dataset.qflowPalette = appearance.palette;
  root.dataset.qflowFont = appearance.font;
  root.dataset.qflowAccent = appearance.accent;
  root.dataset.qflowSurface = appearance.surface_style;
  return appearance;
}

export function announceAppearance(value) {
  const appearance = applyAppearance(value);
  window.dispatchEvent(new CustomEvent('qflow:appearance-updated', { detail: appearance }));
  return appearance;
}
